/**
 * Login entry point.
 *
 * Renders a minimal sign-in screen using the shared AuthModule.
 * After successful Cognito auth, sets the ID token as a cookie
 * and redirects to the main taskpane.
 */

import * as React from "react";
import { createRoot } from "react-dom/client";
import { FluentProvider } from "@fluentui/react-components";
import { paebblDarkTheme } from "../taskpane/theme/paebblTheme";
import { AuthModule } from "../taskpane/services/authModule";
import { loadConfig } from "../taskpane/config";
import AuthScreen from "../taskpane/components/AuthScreen";
import { AuthStatus } from "../taskpane/types";
import "../taskpane/styles/paebbl/index.css";

const COOKIE_NAME = "xlai-id-token";

function setCookie(name: string, value: string, maxAgeSecs: number): void {
  document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSecs}; secure; samesite=none`;
}

const auth = new AuthModule();

const LoginApp: React.FC = () => {
  const [status, setStatus] = React.useState<AuthStatus>("signed_out");
  const [error, setError] = React.useState<string | null>(null);

  const handleSignIn = async () => {
    try {
      setStatus("authenticating");
      const tokens = await auth.signIn();
      const expiresIn = Math.floor((tokens.expiresAt - Date.now()) / 1000);
      setCookie(COOKIE_NAME, tokens.idToken, expiresIn);
      sessionStorage.setItem("xlai-tokens", JSON.stringify(tokens));
      window.location.href = "/taskpane.html";
    } catch (e) {
      setStatus("error");
      setError(e instanceof Error ? e.message : "Sign-in failed.");
    }
  };

  const handleSignOut = () => {
    auth.signOut();
    setStatus("signed_out");
  };

  return (
    <AuthScreen
      status={status}
      error={error}
      onSignIn={handleSignIn}
      onSignOut={handleSignOut}
    />
  );
};

const rootElement = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

Office.onReady(async () => {
  await loadConfig();
  root?.render(
    <FluentProvider theme={paebblDarkTheme}>
      <LoginApp />
    </FluentProvider>,
  );
});
